import { Box, Divider, Typography } from '@mui/material'

import { currencyFormatter } from 'helpers'

interface ConsignmentFeeTooltipProps {
  tariffData: any
  distanceValue: number
  returnDistanceValue: number
  isWaitAndReturn: boolean
  consignmentFeeFormatted: string
  otherChargesFormatted: string
  revenueFormatted: string
}

export const ConsignmentFeeTooltip = ({
  tariffData,
  distanceValue,
  returnDistanceValue,
  isWaitAndReturn,
  consignmentFeeFormatted,
  otherChargesFormatted,
  revenueFormatted,
}: ConsignmentFeeTooltipProps) => {
  if (!tariffData?.tariffById || !distanceValue) {
    return (
      <Box maxWidth={320}>
        <Typography fontWeight={600} gutterBottom>
          Revenue
        </Typography>

        <Typography variant='body2'>Consignment Fee: {consignmentFeeFormatted}</Typography>
        <Typography variant='body2'>Other Charges: {otherChargesFormatted}</Typography>

        <Divider sx={{ my: 1 }} />

        <Typography fontWeight={700}>Total: {revenueFormatted}</Typography>
      </Box>
    )
  }

  const { minMiles, minCharge, rate } = tariffData.tariffById

  const effectiveDistance = isWaitAndReturn
    ? distanceValue - (returnDistanceValue || 0)
    : distanceValue

  const isWithinMin = effectiveDistance <= minMiles
  const extraMiles = Math.max(effectiveDistance - minMiles, 0)
  const extraMilesCharge = extraMiles * rate

  return (
    <Box maxWidth={380}>
      <Typography fontWeight={600} gutterBottom>
        Total Consignment Fee Breakdown
      </Typography>

      {/* Distance Section */}
      <Typography fontWeight={600} variant='body2' gutterBottom>
        Distance
      </Typography>
      <Typography variant='body2'>
        Effective Distance: {effectiveDistance.toFixed(2)} miles
      </Typography>
      <Typography variant='body2'>Minimum Miles: {minMiles} miles</Typography>

      <Divider sx={{ my: 1 }} />

      {/* Charges Section */}
      <Typography fontWeight={600} variant='body2' gutterBottom>
        Charges
      </Typography>

      <Typography variant='body2'>Minimum Charge: {currencyFormatter.format(minCharge)}</Typography>

      {!isWithinMin && (
        <>
          <Typography variant='body2' sx={{ mb: 0.5 }}>
            Extra Miles = {effectiveDistance.toFixed(2)} - {minMiles} = {extraMiles.toFixed(2)}{' '}
            miles
          </Typography>
          <Typography variant='body2'>Rate per Mile: {currencyFormatter.format(rate)}</Typography>
          <Typography variant='body2' sx={{ mt: 0.5 }}>
            Extra Miles Charge = {extraMiles.toFixed(2)} × {currencyFormatter.format(rate)} ={' '}
            {currencyFormatter.format(extraMilesCharge)}
          </Typography>
        </>
      )}

      <Divider sx={{ my: 1 }} />

      {/* Calculation Section */}
      <Typography fontWeight={600} variant='body2' gutterBottom>
        Calculation
      </Typography>

      {isWithinMin ? (
        <Typography variant='body2'>
          Since {effectiveDistance.toFixed(2)} ≤ {minMiles} miles:
          <br />
          Consignment Fee = {currencyFormatter.format(minCharge)}
        </Typography>
      ) : (
        <Typography variant='body2'>
          {currencyFormatter.format(minCharge)} + {currencyFormatter.format(extraMilesCharge)} ={' '}
          {consignmentFeeFormatted}
        </Typography>
      )}

      <Divider sx={{ my: 1 }} />

      {/* Result Section */}
      <Typography fontWeight={600} variant='body2' gutterBottom>
        Revenue
      </Typography>
      <Typography variant='body2'>Consignment Fee: {consignmentFeeFormatted}</Typography>
      <Typography variant='body2'>Other Charges: {otherChargesFormatted}</Typography>

      <Typography variant='body2' sx={{ mt: 1 }}>
        {consignmentFeeFormatted} + {otherChargesFormatted} = {revenueFormatted}
      </Typography>

      <Typography fontWeight={700} mt={1}>
        Total Revenue: {revenueFormatted}
      </Typography>
    </Box>
  )
}
